import type { AttackVectorStatus, Finding, ScanEvent, ScanGraphNode } from "../api";
import type { Checkpoint } from "./demoTypes";

/** The sample company. The onboarding form is pre-filled with the host; the
 *  user may type their own, and every fixture takes it as a parameter. */
export const DEMO_HOST = "mycoolshop.example";

/** Fixed ids, far from anything real, so anchors and routes can name them. */
export const DEMO_IDS = {
  project: "demo-project-acme",
  target: "demo-target-acme",
  scan: "demo-scan-acme",
  pentester: "demo-pentester",
  notifFinding: "demo-notif-finding",
  notifReport: "demo-notif-report",
} as const;

export const DEMO_VERIFY_TOKEN = "backdoor-verify=7f3a9c2e-4b1d-demo";

export const DEMO_PIPELINE = { name: "mission_web_engagement", version: "2.4.1" } as const;

const iso = (ms: number): string => new Date(ms).toISOString();

/** Stage checkpoints of the demo scan. `elapsedMin` is how long a real scan
 *  has run by then — the timer jumps to it on every Next. `currentNode` is
 *  the engine's active step (ScanBar anchors the active stage off it). */
export const CHECKPOINTS: ReadonlyArray<{
  key: "recon" | "map" | "analyze" | "exploit" | "chain" | "report";
  progress: number;
  currentNode: string | null;
  elapsedMin: number;
}> = [
  { key: "recon", progress: 8, currentNode: "recon.subfinder", elapsedMin: 1 },
  { key: "map", progress: 31, currentNode: "map.crawl", elapsedMin: 35 },
  { key: "analyze", progress: 49, currentNode: "analyze.hypotheses", elapsedMin: 70 },
  { key: "exploit", progress: 67, currentNode: "exploit.iter_02", elapsedMin: 110 },
  { key: "chain", progress: 85, currentNode: "chain.replay", elapsedMin: 150 },
  { key: "report", progress: 100, currentNode: null, elapsedMin: 186 },
];

export const LAST_CHECKPOINT: Checkpoint = 5;

// ---- Findings ---------------------------------------------------------------
//
// Sample write-ups for the demo UI. Deliberately descriptive: they explain the
// class of issue, where it was, how it was confirmed and how to fix it — the
// product's own output shape — without shipping copy-paste attack strings.

export interface DemoFindingFixture {
  id: string;
  /** Checkpoint the finding lands at. */
  at: Checkpoint;
  /** Hypothesis it confirms (payload.lead_id on the finding event), if any. */
  vectorId: string | null;
  title: string;
  severity: "critical" | "high" | "medium" | "low";
  vuln_type: string;
  path: string;
  tool_name: string;
  score: number;
  cvss: string;
  description: (host: string) => string;
  poc: (host: string) => string;
  remediation: string;
  /** Minutes after scan start the finding was recorded. */
  minute: number;
}

export const DEMO_FINDINGS: DemoFindingFixture[] = [
  {
    id: "demo-finding-sqli",
    at: 3,
    vectorId: "vec-sqli",
    title: "SQL injection in product sorting",
    severity: "critical",
    vuln_type: "SQL injection",
    path: "/api/v1/products",
    tool_name: "agent",
    score: 9.8,
    cvss: "CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H",
    description: (host) =>
      `The product-listing endpoint on https://${host} builds its ORDER BY clause from the sort parameter without parameterisation. An unauthenticated request can influence the query and, through a time-based blind technique, read other tables. The agent confirmed read access to the orders and customers tables (names, emails, shipping addresses). Severity is critical because no authentication is required and customer data is exposed.`,
    poc: () =>
      "A sort value carrying a database delay function made the response consistently slow (about 5 s vs a 120 ms baseline) across repeated requests, confirming the parameter reaches the SQL engine.",
    remediation:
      "Stop building SQL from user input. Map the sort parameter onto an allow-list of column names and pass the direction as a fixed ASC/DESC literal or a bound parameter. Run the API's database role with least privilege (read-only where possible). A WAF rule blocking database-function keywords in query strings is a reasonable stop-gap until the fix ships.",
    minute: 98,
  },
  {
    id: "demo-finding-idor",
    at: 4,
    vectorId: "vec-idor",
    title: "Any customer's order readable by id (IDOR)",
    severity: "high",
    vuln_type: "Insecure direct object reference",
    path: "/api/v1/orders/{id}",
    tool_name: "agent",
    score: 8.2,
    cvss: "CVSS:3.1/AV:N/AC:L/PR:L/UI:N/S:U/C:H/I:N/A:N",
    description: (host) =>
      `The order-detail endpoint on https://${host} returns a full order — items, totals, shipping address, last four card digits — for any numeric id, as long as the caller holds any valid session. Ids are sequential, so the whole order history can be walked. The agent, signed in as a test customer, retrieved another customer's order and address.`,
    poc: () =>
      "Signed in as the test customer, requesting a neighbouring order id returned 200 with a different customer's details instead of 403/404.",
    remediation:
      "Scope every order lookup to the authenticated customer (match both the order id and the owner) and return 404 for orders that are not theirs. Prefer non-sequential public identifiers (such as UUIDv7) so ids cannot be enumerated. Add an authorisation test to the order endpoints' suite.",
    minute: 131,
  },
  {
    id: "demo-finding-git",
    at: 4,
    vectorId: "vec-git",
    title: "Source code disclosure via exposed .git directory",
    severity: "high",
    vuln_type: "Source code disclosure",
    path: "/.git/",
    tool_name: "agent",
    score: 7.5,
    cvss: "CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:N/A:N",
    description: (host) =>
      `The web server at https://${host} serves the repository's .git directory. Its metadata is readable, so the storefront source can be reconstructed, including the module with the vulnerable product query and an example environment file that lists the names of production secrets.`,
    poc: () =>
      "A request for the repository's HEAD metadata file returned 200 with a valid Git ref instead of 403/404.",
    remediation:
      "Deny access to /.git and other dotfiles at the web server or CDN, and deploy from build artefacts rather than a checked-out repository. Rotate any secret that was ever committed, since the history is now considered public.",
    minute: 136,
  },
  {
    id: "demo-finding-xss",
    at: 4,
    vectorId: "vec-xss",
    title: "Reflected XSS in site search",
    severity: "medium",
    vuln_type: "Cross-site scripting",
    path: "/search",
    tool_name: "agent",
    score: 6.1,
    cvss: "CVSS:3.1/AV:N/AC:L/PR:N/UI:R/S:C/C:L/I:L/A:N",
    description: (host) =>
      `The search results heading on https://${host} echoes the query back without HTML encoding, so a crafted link can run script in the victim's session. The impact is raised because the session cookie is not marked HttpOnly, which puts the session within reach of injected script.`,
    poc: () =>
      "A query containing HTML markup was reflected into the page unescaped, and a harmless marker payload executed in the agent's test browser.",
    remediation:
      "HTML-encode the query when rendering it (the framework's default escaping was bypassed by a raw-HTML render in the search header). Mark the session cookie HttpOnly and SameSite=Lax, and add a Content-Security-Policy that disallows inline script.",
    minute: 139,
  },
  {
    id: "demo-finding-jquery",
    at: 4,
    vectorId: null,
    title: "Outdated jQuery with known vulnerabilities",
    severity: "low",
    vuln_type: "Vulnerable component",
    path: "/static/js/vendor/jquery.min.js",
    tool_name: "agent",
    score: 3.7,
    cvss: "CVSS:3.1/AV:N/AC:L/PR:N/UI:R/S:C/C:L/I:L/A:N",
    description: (host) =>
      `https://${host} loads jQuery 1.12.4 (2016), affected by CVE-2020-11022 and CVE-2020-11023 (XSS through untrusted HTML passed to the library's DOM methods). No exploitable sink was found on this site, so the finding is rated low.`,
    poc: () => "The site's reported jQuery version string was 1.12.4.",
    remediation:
      "Upgrade to a current jQuery release or remove it: the storefront is a React application and the library is only used by one legacy widget. Add dependency scanning to CI so end-of-life libraries surface before release.",
    minute: 141,
  },
];

export function findingFixture(id: string): DemoFindingFixture | undefined {
  return DEMO_FINDINGS.find((f) => f.id === id);
}

/** Findings as the API returns them, those recorded by checkpoint `k`. */
export function demoFindings(host: string, scanId: string, k: Checkpoint, startedAtMs: number): Finding[] {
  return DEMO_FINDINGS.filter((f) => f.at <= k).map((f) => ({
    finding_id: f.id,
    scan_id: scanId,
    target_id: DEMO_IDS.target,
    title: f.title,
    description: f.description(host),
    severity: f.severity,
    vuln_type: f.vuln_type,
    target: f.path,
    target_url: `https://${host}${f.path}`,
    target_host: host,
    tool_name: f.tool_name,
    is_verified: true,
    is_false_positive: false,
    remediation: f.remediation,
    created_at: iso(startedAtMs + f.minute * 60_000),
    updated_at: iso(startedAtMs + f.minute * 60_000),
    status: "confirmed",
    score: f.score,
    poc: f.poc(host),
    cvss: f.cvss,
  }));
}

// ---- Hypotheses (attack vectors) ----------------------------------------------
//
// The vectors the agent planned during ANALYZE, emitted into the event feed so
// the existing deriveAttackVectorsFromEvents() reconstructs them. `how_to_test`
// is an approach, not a script.

export interface DemoVectorFixture {
  id: string;
  vuln_type: string;
  path: string;
  severity: "critical" | "high" | "medium";
  reason: string;
  how_to_test: string;
  /** Status by checkpoint (ANALYZE onwards). */
  status: Record<2 | 3 | 4 | 5, AttackVectorStatus>;
}

export const DEMO_VECTORS: DemoVectorFixture[] = [
  {
    id: "vec-sqli",
    vuln_type: "SQL injection",
    path: "/api/v1/products",
    severity: "critical",
    reason: "The sort parameter looks concatenated into SQL: an invalid value leaks a database syntax error.",
    how_to_test: "Compare result sets for boolean conditions, then confirm with a timing signal; read schema from error text.",
    status: { 2: "assigned", 3: "confirmed", 4: "confirmed", 5: "confirmed" },
  },
  {
    id: "vec-idor",
    vuln_type: "Insecure direct object reference",
    path: "/api/v1/orders/{id}",
    severity: "high",
    reason: "Order ids are sequential and the endpoint shows no ownership check in the OpenAPI spec.",
    how_to_test: "Request neighbouring order ids with a second customer's session; compare the responses.",
    status: { 2: "pending", 3: "assigned", 4: "confirmed", 5: "confirmed" },
  },
  {
    id: "vec-xss",
    vuln_type: "Cross-site scripting",
    path: "/search",
    severity: "medium",
    reason: "The query is reflected in the results heading; the response is HTML with no content security policy.",
    how_to_test: "Reflect a harmless marker, then check whether markup in the query renders instead of escaping.",
    status: { 2: "pending", 3: "pending", 4: "confirmed", 5: "confirmed" },
  },
  {
    id: "vec-admin",
    vuln_type: "Authentication bypass",
    path: "/admin",
    severity: "high",
    reason: "/admin redirects to login, but its JS bundle and API prefix are served without a session.",
    how_to_test: "Call the admin API prefix directly and try role-override headers and stale token variants.",
    status: { 2: "pending", 3: "pending", 4: "tested_no_finding", 5: "tested_no_finding" },
  },
  {
    id: "vec-git",
    vuln_type: "Source code disclosure",
    path: "/.git/",
    severity: "high",
    reason: "The repository HEAD metadata answered 200 during directory brute force.",
    how_to_test: "Fetch refs and objects, reconstruct the tree, review it for secrets and the product query.",
    status: { 2: "pending", 3: "pending", 4: "confirmed", 5: "confirmed" },
  },
];

export function vectorStatusAt(v: DemoVectorFixture, k: Checkpoint): AttackVectorStatus | null {
  if (k < 2) return null;
  return v.status[k as 2 | 3 | 4 | 5];
}

// ---- Events -------------------------------------------------------------------

export interface DemoEventFixture {
  id: string;
  at: Checkpoint;
  /** Minutes after scan start. */
  minute: number;
  event_type: string;
  phase: string;
  node_type?: string;
  message: (host: string) => string;
  payload?: (host: string, k: Checkpoint) => Record<string, unknown>;
}

const vectorEvent = (v: DemoVectorFixture, minute: number): DemoEventFixture => ({
  id: `demo-ev-hyp-${v.id}`,
  at: 2,
  minute,
  event_type: "agent.hypothesis",
  phase: "analyze",
  message: (host) => `Hypothesis: ${v.vuln_type} on https://${host}${v.path}`,
  payload: (host, k) => ({
    vector_id: v.id,
    vuln_type: v.vuln_type,
    host,
    target: `https://${host}${v.path}`,
    severity: v.severity,
    reason: v.reason,
    how_to_test: v.how_to_test,
    status: vectorStatusAt(v, k) ?? "pending",
  }),
});

const findingEvent = (f: DemoFindingFixture): DemoEventFixture => ({
  id: `demo-ev-finding-${f.id}`,
  at: f.at,
  minute: f.minute,
  event_type: "finding.added",
  phase: f.at >= 4 ? "chain" : "exploit",
  message: (host) =>
    `${f.severity[0].toUpperCase()}${f.severity.slice(1)}: ${f.title} — https://${host}${f.path}`,
  payload: (host) => ({
    ...(f.vectorId ? { lead_id: f.vectorId, vuln_type: f.vuln_type } : {}),
    finding_id: f.id,
    target: `https://${host}${f.path}`,
    severity: f.severity,
    status: "confirmed",
  }),
});

export const DEMO_EVENTS: DemoEventFixture[] = [
  // 0 — RECON
  { id: "demo-ev-start", at: 0, minute: 0, event_type: "scan.started", phase: "recon", message: (h) => `Scan launched on ${h} — time to find the cracks 🚀` },
  {
    id: "demo-ev-recon-plan", at: 0, minute: 0.3, event_type: "llm.response_received", phase: "recon", node_type: "llm.complete",
    message: () => "Reconnaissance plan",
    payload: () => ({ response_text: `Mapping the perimeter first — every subdomain, port and piece of the stack. Nothing touches the app until I know the whole surface.` }),
  },
  { id: "demo-ev-subfinder", at: 0, minute: 0.6, event_type: "agent.tool_executed", phase: "recon", node_type: "execution.tool.subfinder", message: () => `Found 7 subdomains — including a tempting "admin" and a "staging"` },
  { id: "demo-ev-live", at: 0, minute: 0.9, event_type: "live_targets.derived", phase: "recon", message: () => "7 live hosts, all answering on HTTPS", payload: () => ({ hosts: 7, urls: 0 }) },
  // 1 — MAP
  { id: "demo-ev-nmap", at: 1, minute: 6, event_type: "agent.tool_executed", phase: "recon", node_type: "execution.tool.nmap", message: () => "Ports mapped: 443 open (nginx 1.24), 80 → HTTPS, SSH locked down" },
  { id: "demo-ev-tech", at: 1, minute: 9, event_type: "agent.tool_executed", phase: "recon", node_type: "execution.tool.webanalyze", message: () => "Stack sniffed out: Next.js 14, React, Stripe — and jQuery 1.12.4 from 2016 👀" },
  { id: "demo-ev-crawl", at: 1, minute: 18, event_type: "browser.authenticated_crawl", phase: "scanner", message: () => "Crawled 42 pages — catalogue, cart, checkout, account", payload: () => ({ routes: 42 }) },
  { id: "demo-ev-routes", at: 1, minute: 24, event_type: "api_route.classify", phase: "scanner", message: () => "58 API routes mapped — and the OpenAPI spec is wide open", payload: () => ({ routes: 58 }) },
  { id: "demo-ev-ffuf", at: 1, minute: 29, event_type: "agent.tool_executed", phase: "scanner", node_type: "execution.tool.ffuf", message: () => "/admin hides behind login, but /.git is reachable — a source-code leak waiting to happen" },
  {
    id: "demo-ev-map-summary", at: 1, minute: 33, event_type: "llm.response_received", phase: "scanner", node_type: "llm.complete",
    message: () => "Attack surface mapped",
    payload: () => ({ response_text: "Surface mapped. Checkout, orders and search look the juiciest — and that exposed .git is basically a gift." }),
  },
  // 2 — ANALYZE
  {
    id: "demo-ev-plan", at: 2, minute: 52, event_type: "llm.response_received", phase: "analyze", node_type: "llm.complete",
    message: () => "Attack plan",
    payload: (h) => ({
      tool_calls_count: 0,
      response_text: JSON.stringify({
        requests: DEMO_VECTORS.map((v) => ({ target: `https://${h}${v.path}`, family: v.vuln_type, priority: v.severity === "medium" ? "medium" : "high", reason: v.reason })),
      }),
    }),
  },
  ...DEMO_VECTORS.map((v, i) => vectorEvent(v, 55 + i * 2)),
  { id: "demo-ev-payloads", at: 2, minute: 66, event_type: "agent.tool_executed", phase: "analyze", node_type: "execution.payload_matrix", message: () => "Payloads loaded — 5 vectors, ready to fire" },
  // 3 — EXPLOIT
  {
    id: "demo-ev-sqli-think", at: 3, minute: 84, event_type: "llm.response_received", phase: "exploit", node_type: "agent.mission_thinking",
    message: () => "Poking at the product sort parameter",
    payload: () => ({ tool_calls_count: 2, response_text: "The product sort parameter bites back — my input is reaching the database. Let me confirm it before I call it." }),
  },
  { id: "demo-ev-sqlmap", at: 3, minute: 96, event_type: "agent.tool_executed", phase: "exploit", node_type: "agent.mission_iteration", message: () => "Boom 💥 the sort parameter is injectable — 5-second delay, 3 of 3. The database is talking." },
  findingEvent(DEMO_FINDINGS[0]),
  { id: "demo-ev-idor-start", at: 3, minute: 104, event_type: "agent.tool_executed", phase: "exploit", node_type: "agent.mission_iteration", message: () => "Next: can I read someone else's order? Trying neighbouring order ids…" },
  // 4 — CHAIN
  findingEvent(DEMO_FINDINGS[1]),
  findingEvent(DEMO_FINDINGS[2]),
  findingEvent(DEMO_FINDINGS[3]),
  findingEvent(DEMO_FINDINGS[4]),
  { id: "demo-ev-admin", at: 4, minute: 143, event_type: "agent.tool_executed", phase: "exploit", node_type: "agent.mission_iteration", message: () => "Hammered the admin login — it holds. No way in there." },
  {
    id: "demo-ev-chain-think", at: 4, minute: 146, event_type: "llm.response_received", phase: "chain", node_type: "agent.mission_thinking",
    message: () => "Chaining the findings",
    payload: () => ({ tool_calls_count: 1, response_text: "Chaining it: the SQL injection reads the orders table, the IDOR hands the same data over without a login. Together — an anonymous visitor could walk off with every customer's order history." }),
  },
  { id: "demo-ev-audit", at: 4, minute: 149, event_type: "mission.audit_summary", phase: "chain", message: () => "Mission audit: 5 vectors tested, 4 confirmed, 1 clean attack chain" },
  // 5 — REPORT
  { id: "demo-ev-report", at: 5, minute: 182, event_type: "agent.tool_executed", phase: "report", node_type: "persistence.report", message: () => "Writing it up 📝 — summary, 5 findings, proof and fixes" },
  { id: "demo-ev-done", at: 5, minute: 186, event_type: "scan.completed", phase: "report", message: () => "Done! 🎉 5 findings — 1 critical, 2 high, 1 medium, 1 low" },
];

/** Events up to checkpoint `k` in scan order (oldest first), `revealed` of
 *  the current checkpoint's own events included. */
export function demoEvents(host: string, scanId: string, k: Checkpoint, startedAtMs: number, revealed: number): ScanEvent[] {
  const earlier = DEMO_EVENTS.filter((e) => e.at < k);
  const current = DEMO_EVENTS.filter((e) => e.at === k).slice(0, Math.max(0, revealed));
  return [...earlier, ...current].map((e, i) => ({
    event_id: e.id,
    scan_id: scanId,
    event_type: e.event_type,
    sequence_number: String(i + 1),
    phase: e.phase,
    node_type: e.node_type,
    message: e.message(host),
    payload: e.payload?.(host, k),
    timestamp: iso(startedAtMs + e.minute * 60_000),
  }));
}

export function eventsAtCheckpoint(k: Checkpoint): number {
  return DEMO_EVENTS.filter((e) => e.at === k).length;
}

// ---- Graph --------------------------------------------------------------------
//
// A small, readable pipeline tree (unlike the ~500-node real mission graph).
// Node statuses follow the checkpoint: stages before the current one are
// completed, the current stage is partly done with one node running, later
// stages are pending.

/** A compact, readable pipeline: the six stages flow top-to-bottom, with the
 *  exploit stage branching into the attack vectors and converging back — so the
 *  graph fills the view with legible nodes instead of a sprawl of tiny ones. */
interface GNode {
  id: string;
  type: string;
  label: string;
  /** Checkpoint at which this node is the one running. */
  runAt: Checkpoint;
}

const GRAPH_NODES: GNode[] = [
  { id: "recon", type: "compound.stage", label: "Recon", runAt: 0 },
  { id: "map", type: "compound.stage", label: "Map", runAt: 1 },
  { id: "analyze", type: "compound.stage", label: "Analyze", runAt: 2 },
  { id: "exploit", type: "compound.stage", label: "Exploit", runAt: 3 },
  { id: "v_sqli", type: "agent.mission_iteration", label: "SQL injection", runAt: 3 },
  { id: "v_idor", type: "agent.mission_iteration", label: "IDOR", runAt: 4 },
  { id: "v_xss", type: "agent.mission_iteration", label: "XSS", runAt: 4 },
  { id: "v_git", type: "agent.mission_iteration", label: ".git exposure", runAt: 4 },
  { id: "chain", type: "flow.replay", label: "Chain & verify", runAt: 4 },
  { id: "report", type: "persistence.report", label: "Report", runAt: 5 },
];

const GRAPH_LINKS: Array<[string, string]> = [
  ["recon", "map"],
  ["map", "analyze"],
  ["analyze", "exploit"],
  ["exploit", "v_sqli"],
  ["exploit", "v_idor"],
  ["exploit", "v_xss"],
  ["exploit", "v_git"],
  ["v_sqli", "chain"],
  ["v_idor", "chain"],
  ["v_xss", "chain"],
  ["v_git", "chain"],
  ["chain", "report"],
];

function graphStatus(node: GNode, k: Checkpoint, completed: boolean): string {
  if (completed) return "completed";
  if (k > node.runAt) return "completed";
  if (k === node.runAt) return "running";
  return "pending";
}

export function demoGraphNodes(k: Checkpoint, completed: boolean): ScanGraphNode[] {
  return GRAPH_NODES.map((n) => ({
    id: n.id,
    type: n.type,
    label: n.label,
    status: graphStatus(n, k, completed),
    duration_ms: graphStatus(n, k, completed) === "completed" ? 42_000 : 0,
    started_at: null,
    finished_at: null,
    error_message: "",
    iter_total: 0,
    iter_done: 0,
    is_compound: false,
    event_count: 0,
    children: [],
  }));
}

export function demoGraphEdges(): Array<{ from: string; to: string; kind: string }> {
  return GRAPH_LINKS.map(([from, to]) => ({ from, to, kind: "flow" }));
}

// ---- Notifications ------------------------------------------------------------

export interface DemoNotifFixture {
  id: string;
  at: Checkpoint;
  minute: number;
  event_type: string;
  title: (host: string) => string;
  body: (host: string) => string;
  findingId?: string;
}

export const DEMO_NOTIFS: DemoNotifFixture[] = [
  {
    id: DEMO_IDS.notifFinding,
    at: 3,
    minute: 98,
    event_type: "finding.critical",
    title: () => "Critical finding",
    body: (host) => `SQL injection confirmed on ${host}. Customer data is reachable without authentication.`,
    findingId: "demo-finding-sqli",
  },
  {
    id: DEMO_IDS.notifReport,
    at: 5,
    minute: 186,
    event_type: "report.ready",
    title: () => "Report ready",
    body: (host) => `The scan of ${host} finished with 5 findings. Your report is ready to read and download.`,
  },
];

export function demoNotifications(host: string, k: Checkpoint, startedAtMs: number, readIds: string[]) {
  return DEMO_NOTIFS.filter((n) => n.at <= k).map((n) => ({
    notification_id: n.id,
    user_id: "demo-user",
    event_type: n.event_type,
    content: {
      title: n.title(host),
      body: n.body(host),
      ...(n.findingId ? { action_url: "/findings", action_label: "View finding" } : {}),
    },
    context: {
      scan_id: DEMO_IDS.scan,
      project_id: DEMO_IDS.project,
      ...(n.findingId ? { finding_id: n.findingId } : {}),
    },
    channels: ["websocket"],
    delivery_status: "delivered",
    is_read: readIds.includes(n.id),
    created_at: iso(startedAtMs + n.minute * 60_000),
    read_at: readIds.includes(n.id) ? iso(startedAtMs + n.minute * 60_000) : null,
  }));
}

// ---- Subscription -------------------------------------------------------------
//
// A "Demo" plan with unlimited scans so the paywall and plan redirects stay
// out of the tour's way.

export function demoSubscription() {
  const now = Date.now();
  const end = new Date(now + 1000 * 60 * 60 * 24 * 30).toISOString();
  return {
    subscription: {
      subscription_id: "demo-subscription",
      plan_tier: "demo",
      status: "active",
      provider_subscription_id: "demo",
      current_period_start: new Date(now).toISOString(),
      current_period_end: end,
    },
    plan: {
      plan_tier: "demo",
      display_name: "Demo",
      description: "Interactive product tour",
      prices: [],
      max_scans_per_month: -1,
    },
    current_usage: { scans_this_month: 0 },
  };
}
